import { Component, OnInit, OnDestroy, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { StrategiesService } from '../../services/requests/strategies.service';
import { DevicesService } from '../../services/requests/devices.service';
import { ModalClickOutsideService } from '../../services/modal-click-outside.service';

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-strategies',
  templateUrl: './strategies.component.html',
  styleUrls: ['./strategies.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class StrategiesComponent implements OnInit, OnDestroy, AfterViewInit {
  strategies: any[] = [];
  filteredStrategies: any[] = [];
  devices: any[] = [];
  searchTerm: string = '';
  archiveFilter: 'active' | 'archived' | 'all' = 'active';
  typeStrategieFilter: 'all' | 'court terme' | 'long terme' = 'all';
  isAdmin = false;
  isSousAdmin = false;
  loading = false;
  isAdding = false;
  isUpdating = false;
  strategy: any = {
    operateur: '',
    prixCarte: 0,
    typeStrategie: '',
    minuteDebutSMS: '',
    minuteFinSMS: '',
    minuteDebutMMS: '',
    minuteFinMMS: '',
    typeEnvoi: '',
    quotaMinSMS: 0,
    quotaHSMS: 0,
    quotaJourSMS: 0,
    quotaFixeSMS: 0,
    quotaMinMMS: 0,
    quotaHMMS: 0,
    quotaJourMMS: 0,
    quotaFixeMMS: 0,
    nbMaxPuceSimultanee: 1,
  };

  editingStrategy: any = null;
  showEditModal = false;
  newStrategy: any = {
    operateur: '',
    prixCarte: 0,
    typeStrategie: '',
    minuteDebutSMS: '',
    minuteFinSMS: '',
    minuteDebutMMS: '',
    minuteFinMMS: '',
    typeEnvoi: '',
    quotaMinSMS: 0,
    quotaHSMS: 0,
    quotaJourSMS: 0,
    quotaFixeSMS: 0,
    quotaMinMMS: 0,
    quotaHMMS: 0,
    quotaJourMMS: 0,
    quotaFixeMMS: 0,
    nbMaxPuceSimultanee: 1,
  };
  showAddModal = false;
  showModal = false;
  modalConfig: any = {
    title: 'Supprimer la stratégie',
    confirmText: 'Supprimer',
    cancelText: 'Annuler',
    confirmButtonClass: 'modal-btn-delete',
    icon: 'warning',
    iconColor: 'red'
  };
  strategyToDelete: any = null;
  isDeleting = false;

  @ViewChild('editModalContent', { static: false }) editModalContent!: ElementRef;
  @ViewChild('addModalContent', { static: false }) addModalContent!: ElementRef;

  constructor(
    private strategiesService: StrategiesService,
    private devicesService: DevicesService,
    private modalClickOutsideService: ModalClickOutsideService
  ) {}

  ngOnInit(): void {
    try {
      const savedFilter = localStorage.getItem('localStorage_SP-GetStrategies') as 'active' | 'archived' | 'all' | null;
      if (savedFilter === 'active' || savedFilter === 'archived' || savedFilter === 'all') {
        this.archiveFilter = savedFilter;
      }
    } catch (e) {
      console.error('Erreur lors de la récupération du filtre SP-GetStrategies :', e);
    }

    try {
      const savedTypeFilter = localStorage.getItem('localStorage_SP-GetStrategies_TypeFilter') as 'all' | 'court terme' | 'long terme' | null;
      if (savedTypeFilter === 'all' || savedTypeFilter === 'court terme' || savedTypeFilter === 'long terme') {
        this.typeStrategieFilter = savedTypeFilter;
      }
    } catch (e) {
      console.error('Erreur lors de la récupération du filtre de type SP-GetStrategies :', e);
    }

    this.loadDevices();
    this.loadStrategies();
    this.checkUserRole();
  }

  ngAfterViewInit(): void {
    this.updateModalRegistrations();
  }

  private updateModalRegistrations(): void {
    if (this.editModalContent) {
      if (this.showEditModal) {
        this.modalClickOutsideService.registerModal(this.editModalContent, () => this.closeEditModal());
      } else {
        this.modalClickOutsideService.unregisterModal(this.editModalContent);
      }
    }
    if (this.addModalContent) {
      if (this.showAddModal) {
        this.modalClickOutsideService.registerModal(this.addModalContent, () => this.closeAddModal());
      } else {
        this.modalClickOutsideService.unregisterModal(this.addModalContent);
      }
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this.editModalContent) {
      this.modalClickOutsideService.unregisterModal(this.editModalContent);
    }
    if (this.addModalContent) {
      this.modalClickOutsideService.unregisterModal(this.addModalContent);
    }
  }

  private updateScroll(): void {
    document.body.style.overflow = (this.showEditModal || this.showAddModal || this.showModal) ? 'hidden' : '';
  }

  checkUserRole(): void {
    const userRole = localStorage.getItem('role');
    this.isAdmin = userRole === 'admin';
    this.isSousAdmin = userRole === 'sousAdmin';
  }

  loadStrategies(): void {
    this.loading = true;
    this.strategiesService.getAll().subscribe((res: any) => {
      this.loading = false;
      if (res.status) {
        this.strategies = (res.data?.strategies || []).map((strategy: any) => ({
          ...strategy,
          isArchive: strategy.isArchive !== undefined ? strategy.isArchive : false
        }));
        this.applyFilter();
      }
    });
  }

  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    const filteredByArchive = this.strategies.filter(strategy => {
      const isArchived = !!strategy.isArchive;
      if (this.archiveFilter === 'active') {
        return !isArchived;
      }
      if (this.archiveFilter === 'archived') {
        return isArchived;
      }
      return true;
    });

    const filteredByType = filteredByArchive.filter(strategy => {
      if (this.typeStrategieFilter === 'all') {
        return true;
      }
      return strategy.typeStrategie?.toLowerCase() === this.typeStrategieFilter.toLowerCase();
    });

    if (!term) {
      this.filteredStrategies = [...filteredByType];
    } else {
      this.filteredStrategies = filteredByType.filter(strategy =>
        strategy.operateur?.toLowerCase().includes(term) ||
        strategy.typeStrategie?.toLowerCase().includes(term) ||
        strategy.typeEnvoi?.toLowerCase().includes(term)
      );
    }
  }

  setArchiveFilter(filter: 'active' | 'archived' | 'all'): void {
    if (this.archiveFilter !== filter) {
      this.archiveFilter = filter;
      this.applyFilter();
      try {
        localStorage.setItem('localStorage_SP-GetStrategies', filter);
      } catch (e) {
        console.error('Erreur lors de la sauvegarde du filtre SP-GetStrategies :', e);
      }
    }
  }

  onTypeStrategieFilterChange(): void {
    this.applyFilter();
    try {
      localStorage.setItem('localStorage_SP-GetStrategies_TypeFilter', this.typeStrategieFilter);
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du filtre de type SP-GetStrategies :', e);
    }
  }

  getArchiveCount(type: 'active' | 'archived'): number {
    const term = this.searchTerm.toLowerCase().trim();
    
    return this.strategies.filter(strategy => {
      const isArchived = !!strategy.isArchive;
      const matchesArchive = type === 'active' ? !isArchived : isArchived;
      
      if (!matchesArchive) return false;
      
      if (term) {
        return strategy.operateur?.toLowerCase().includes(term);
      }
      
      return true;
    }).length;
  }

  getFilteredAllCount(): number {
    const term = this.searchTerm.toLowerCase().trim();
    
    if (!term) {
      return this.strategies.length;
    }
    
    return this.strategies.filter(strategy =>
      strategy.operateur?.toLowerCase().includes(term)
    ).length;
  }

  loadDevices(): void {
    this.devicesService.getAll().subscribe({
      next: (response: any) => {
        this.devices = response.data?.devices || [];
      },
      error: (error: any) => {
        console.error('❌ Erreur lors du chargement des devices:', error);
        this.devices = [];
      }
    });
  }

  hasActiveMachines(strategyId: string): boolean {
    if (!strategyId) return false;
    return this.devices.some((d: any) => d.idStrat === strategyId && !d.isArchive);
  }

  toggleArchiveStrategy(strategy: any): void {
    if (!strategy?._id || this.loading) return;

    const newArchiveStatus = !strategy.isArchive;
    if (newArchiveStatus && this.hasActiveMachines(strategy._id)) {
      const activeDevices = this.devices.filter((d: any) => 
        d.idStrat === strategy._id && !d.isArchive
      );
      showNotif(
        `⛔ Impossible d'archiver cette stratégie. Elle est utilisée par ${activeDevices.length} machine${activeDevices.length > 1 ? 's' : ''} active${activeDevices.length > 1 ? 's' : ''}.`,
        'error'
      );
      return;
    }

    this.loading = true;

    this.strategiesService.toggleArchive({
      id: strategy._id,
      isArchive: newArchiveStatus
    }).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.status) {
          strategy.isArchive = newArchiveStatus;
          showNotif(
            newArchiveStatus 
              ? '✅ Stratégie archivée avec succès' 
              : '✅ Stratégie désarchivée avec succès',
            'success'
          );
          this.applyFilter();
        } else {
          showNotif(response.message || '❌ Erreur lors de l\'archivage', 'error');
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('❌ Erreur toggleArchiveStrategy:', error);
        showNotif(error?.error?.message || '❌ Erreur serveur lors de l\'archivage', 'error');
      }
    });
  }

  getFilteredStrategiesCount(): number {
    return this.filteredStrategies.length;
  }

  addOrUpdate(): void {
    const nbSimultanee = +this.strategy.nbMaxPuceSimultanee || 1;
    
    if (isNaN(nbSimultanee) || nbSimultanee < 1) {
      showNotif('⛔ Entrez un nombre valide (>= 1).', 'error');
      return;
    }

    const isLongTerme = this.strategy.typeStrategie === 'long terme';
    const parseMinutes = (minutes: string) => isLongTerme ? [] : (minutes ? minutes.split(',').map((m: string) => +m.trim()) : []);
    
    const data = {
      ...this.strategy,
      id: this.strategy._id,
      nbMaxPuceSimultanee: nbSimultanee,
      minuteDebutSMS: parseMinutes(this.strategy.minuteDebutSMS),
      minuteFinSMS: parseMinutes(this.strategy.minuteFinSMS),
      minuteDebutMMS: parseMinutes(this.strategy.minuteDebutMMS),
      minuteFinMMS: parseMinutes(this.strategy.minuteFinMMS)
    };

    const isUpdate = !!this.strategy._id;
    if (isUpdate) {
      this.isUpdating = true;
    } else {
      this.isAdding = true;
    }

    const action = isUpdate ? this.strategiesService.update(data) : this.strategiesService.add(data);
    action.subscribe({
      next: (response: any) => {
        this.isUpdating = false;
        this.isAdding = false;
        if (response.status) {
          showNotif(isUpdate ? '✅ Stratégie mise à jour avec succès' : '✅ Stratégie créée avec succès', 'success');
          this.loadStrategies();
          this.strategy = {
            operateur: '', typeStrategie: '', minuteDebutSMS: '', minuteFinSMS: '',
            minuteDebutMMS: '', minuteFinMMS: '', typeEnvoi: '',
            quotaSMS: { parMinute: 0, parHeure: 0, parJour: 0, parSemaine: 0 },
            quotaMMS: { parMinute: 0, parHeure: 0, parJour: 0, parSemaine: 0 },
            nbMaxPuceSimultanee: ''
          };
        } else {
          showNotif(response.message || (isUpdate ? '❌ Erreur lors de la mise à jour' : '❌ Erreur lors de la création'), 'error');
        }
      },
      error: (error: any) => {
        this.isUpdating = false;
        this.isAdding = false;
        console.error('Erreur stratégie:', error);
        showNotif(error?.error?.message || (isUpdate ? '❌ Erreur serveur mise à jour' : '❌ Erreur serveur création'), 'error');
      }
    });
  }

  openEditModal(strategy: any): void {
    this.editingStrategy = { 
      ...strategy,
      typeStrategie: strategy.typeStrategie || '',
      nbMaxPuceSimultanee: strategy.nbMaxPuceSimultanee || 1,
      typeEnvoi: strategy.typeEnvoi || '',
      operateur: strategy.operateur || '',
      prixCarte: strategy.prixCarte || 0,
      minuteDebutSMS: strategy.minuteDebutSMS || '',
      minuteFinSMS: strategy.minuteFinSMS || '',
      minuteDebutMMS: strategy.minuteDebutMMS || '',
      minuteFinMMS: strategy.minuteFinMMS || '',
      quotaMinSMS: strategy.quotaMinSMS || 0,
      quotaHSMS: strategy.quotaHSMS || 0,
      quotaJourSMS: strategy.quotaJourSMS || 0,
      quotaFixeSMS: strategy.quotaFixeSMS || 0,
      quotaMinMMS: strategy.quotaMinMMS || 0,
      quotaHMMS: strategy.quotaHMMS || 0,
      quotaJourMMS: strategy.quotaJourMMS || 0,
      quotaFixeMMS: strategy.quotaFixeMMS || 0
    };
    this.showEditModal = true;
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistrations();
    }, 0);
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingStrategy = null;
    this.updateScroll();
    this.updateModalRegistrations();
  }

  openAddModal(): void {
    this.showAddModal = true;
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistrations();
    }, 0);
  }

  closeAddModal(): void {
    this.showAddModal = false;
    this.resetNewStrategy();
    this.updateScroll();
    this.updateModalRegistrations();
  }

  resetNewStrategy(): void {
    this.newStrategy = {
      operateur: '',
      typeStrategie: '',
      minuteDebutSMS: '',
      minuteFinSMS: '',
      minuteDebutMMS: '',
      minuteFinMMS: '',
      typeEnvoi: '',
      quotaMinSMS: 0,
      quotaHSMS: 0,
      quotaJourSMS: 0,
      quotaFixeSMS: 0,
      quotaMinMMS: 0,
      quotaHMMS: 0,
      quotaJourMMS: 0,
      quotaFixeMMS: 0,
      nbMaxPuceSimultanee: 1,
    };
  }

  saveEdit(): void {
    if (this.editingStrategy) {
      const nbSimultanee = +this.editingStrategy.nbMaxPuceSimultanee || 1;
      
      if (isNaN(nbSimultanee) || nbSimultanee < 1) {
        showNotif('⛔ Entrez un nombre valide (>= 1).', 'error');
        return;
      }

      const prixCarte = +this.editingStrategy.prixCarte || 0;
      if (!prixCarte || prixCarte <= 0) {
        showNotif('⛔ Le prix de la carte est obligatoire et doit être strictement positif (> 0).', 'error');
        return;
      }

      this.loading = true;

      const isLongTerme = this.editingStrategy.typeStrategie === 'long terme';
      const parseMinutes = (minutes: string | any[]) => {
        if (isLongTerme) return [];
        if (typeof minutes === 'string') {
          return minutes.split(',').map((m: string) => +m.trim()).filter((m: number) => !isNaN(m));
        }
        return minutes || [];
      };

      const data = {
        id: this.editingStrategy._id,
        operateur: this.editingStrategy.operateur,
        typeStrategie: this.editingStrategy.typeStrategie,
        typeEnvoi: this.editingStrategy.typeEnvoi,
        nbMaxPuceSimultanee: nbSimultanee,
        prixCarte: prixCarte,
        minuteDebutSMS: parseMinutes(this.editingStrategy.minuteDebutSMS),
        minuteFinSMS: parseMinutes(this.editingStrategy.minuteFinSMS),
        minuteDebutMMS: parseMinutes(this.editingStrategy.minuteDebutMMS),
        minuteFinMMS: parseMinutes(this.editingStrategy.minuteFinMMS),
        quotaMinSMS: +this.editingStrategy.quotaMinSMS || 0,
        quotaHSMS: +this.editingStrategy.quotaHSMS || 0,
        quotaJourSMS: +this.editingStrategy.quotaJourSMS || 0,
        quotaFixeSMS: +this.editingStrategy.quotaFixeSMS || 0,
        quotaMinMMS: +this.editingStrategy.quotaMinMMS || 0,
        quotaHMMS: +this.editingStrategy.quotaHMMS || 0,
        quotaJourMMS: +this.editingStrategy.quotaJourMMS || 0,
        quotaFixeMMS: +this.editingStrategy.quotaFixeMMS || 0
      };

      const validation = this.validateStrategyData(data);
      if (!validation.isValid) {
        showNotif(validation.message || '❌ Données invalides', 'error');
        return;
      }

      this.strategiesService.update(data).subscribe({
        next: (response: any) => {
          this.loading = false;
          if (response.status) {
            showNotif('✅ Stratégie mise à jour avec succès !', 'success');
            this.loadStrategies();
            this.closeEditModal();
          } else {
            showNotif(response.message || '❌ Erreur lors de la mise à jour de la stratégie', 'error');
          }
        },
        error: (error: any) => {
          this.loading = false;
          console.error('❌ Erreur lors de la mise à jour:', error);
          showNotif(error?.error?.message || '❌ Erreur serveur lors de la mise à jour', 'error');
        }
      });
    }
  }

  addStrategy(): void {
    const nbSimultanee = +this.newStrategy.nbMaxPuceSimultanee || 1;
    
    if (isNaN(nbSimultanee) || nbSimultanee < 1) {
      showNotif('⛔ Entrez un nombre valide (>= 1).', 'error');
      return;
    }

    const prixCarte = +this.newStrategy.prixCarte || 0;
    if (!prixCarte || prixCarte <= 0) {
      showNotif('⛔ Le prix de la carte est obligatoire et doit être strictement positif (> 0).', 'error');
      return;
    }

    this.loading = true;

    const isLongTerme = this.newStrategy.typeStrategie === 'long terme';
    const parseMinutes = (minutes: string | any[]) => {
      if (isLongTerme) return [];
      if (typeof minutes === 'string') {
        return minutes.split(',').map((m: string) => +m.trim()).filter((m: number) => !isNaN(m));
      }
      return minutes || [];
    };

    const data = {
      operateur: this.newStrategy.operateur,
      typeStrategie: this.newStrategy.typeStrategie,
      typeEnvoi: this.newStrategy.typeEnvoi,
      nbMaxPuceSimultanee: nbSimultanee,
      prixCarte: prixCarte,
      minuteDebutSMS: parseMinutes(this.newStrategy.minuteDebutSMS),
      minuteFinSMS: parseMinutes(this.newStrategy.minuteFinSMS),
      minuteDebutMMS: parseMinutes(this.newStrategy.minuteDebutMMS),
      minuteFinMMS: parseMinutes(this.newStrategy.minuteFinMMS),
      quotaMinSMS: +this.newStrategy.quotaMinSMS || 0,
      quotaHSMS: +this.newStrategy.quotaHSMS || 0,
      quotaJourSMS: +this.newStrategy.quotaJourSMS || 0,
      quotaFixeSMS: +this.newStrategy.quotaFixeSMS || 0,
      quotaMinMMS: +this.newStrategy.quotaMinMMS || 0,
      quotaHMMS: +this.newStrategy.quotaHMMS || 0,
      quotaJourMMS: +this.newStrategy.quotaJourMMS || 0,
      quotaFixeMMS: +this.newStrategy.quotaFixeMMS || 0
    };

    const validation = this.validateStrategyData(data);
    if (!validation.isValid) {
      showNotif(validation.message || '❌ Données invalides', 'error');
      return;
    }

    this.strategiesService.add(data).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.status) {
          showNotif('✅ Stratégie ajoutée avec succès !', 'success');
          this.loadStrategies();
          this.closeAddModal();
        } else {
          showNotif(response.message || '❌ Erreur lors de l\'ajout de la stratégie', 'error');
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('❌ Erreur lors de l\'ajout:', error);
        showNotif(error?.error?.message || '❌ Erreur serveur lors de l\'ajout', 'error');
      }
    });
  }

  delete(id: string): void {
    const strategy = this.strategies.find(s => s._id === id);
    if (!strategy) return;

    this.strategyToDelete = strategy;
    this.isDeleting = false;
    this.checkStrategyUsage(id);
  }

  checkStrategyUsage(strategyId: string): void {
    this.loading = true;
    this.devicesService.getAll().subscribe({
      next: (response: any) => {
        this.loading = false;
        const devices = response.data?.devices || [];
        const devicesUsingStrategy = devices.filter((d: any) => d.idStrat === strategyId);
        const isUsed = devicesUsingStrategy.length > 0;

        if (isUsed) {
          this.modalConfig = {
            title: 'Impossible de supprimer',
            message: `La stratégie <strong>${this.strategyToDelete.operateur}</strong> ne peut pas être supprimée car elle est utilisée par <strong>${devicesUsingStrategy.length} machine${devicesUsingStrategy.length > 1 ? 's' : ''}</strong>.`,
            confirmText: 'Supprimer',
            cancelText: 'Fermer',
            confirmButtonClass: 'modal-btn-delete',
            icon: 'error',
            iconColor: 'red',
            warningMessage: '<p class="font-medium mb-1">Veuillez d\'abord retirer cette stratégie des machines qui l\'utilisent avant de pouvoir la supprimer.</p>',
            disabled: true
          };
        } else {
          this.modalConfig = {
            title: 'Supprimer la stratégie',
            message: `Êtes-vous sûr de vouloir supprimer la stratégie <strong>${this.strategyToDelete.operateur}</strong> ?`,
            confirmText: 'Supprimer',
            cancelText: 'Annuler',
            confirmButtonClass: 'modal-btn-delete',
            icon: 'warning',
            iconColor: 'red',
            warningMessage: '<p class="font-medium mb-1">Cette action est irréversible.</p>',
            disabled: false
          };
        }
        this.showModal = true;
        this.updateScroll();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('❌ Erreur vérification devices:', error);
        this.modalConfig = {
          title: 'Supprimer la stratégie',
          message: `Êtes-vous sûr de vouloir supprimer la stratégie <strong>${this.strategyToDelete.operateur}</strong> ?`,
          confirmText: 'Supprimer',
          cancelText: 'Annuler',
          confirmButtonClass: 'modal-btn-delete',
          icon: 'warning',
          iconColor: 'red',
          warningMessage: '<p class="font-medium mb-1">Cette action est irréversible.</p>',
          disabled: false
        };
        this.showModal = true;
        this.updateScroll();
      }
    });
  }

  onModalConfirm(): void {
    if (!this.strategyToDelete || this.modalConfig.disabled) return;
    this.executeDelete(this.strategyToDelete._id);
  }

  onModalClose(): void {
    if (!this.modalConfig.isLoading) {
      this.showModal = false;
      this.strategyToDelete = null;
      this.updateScroll();
    }
  }

  executeDelete(id: string): void {
    this.modalConfig.isLoading = true;
    this.loading = true;
    
    this.strategiesService.delete({ id }).subscribe({
      next: (response: any) => {
        this.modalConfig.isLoading = false;
        this.loading = false;
        if (response.status) {
          this.showModal = false;
          this.updateScroll();
          showNotif('🗑️ Stratégie supprimée avec succès', 'delete');
          this.loadStrategies();
        } else {
          showNotif(response.message || '❌ Erreur lors de la suppression', 'error');
        }
      },
      error: (error: any) => {
        this.modalConfig.isLoading = false;
        this.loading = false;
        console.error('❌ Erreur suppression stratégie:', error);
        
        if (error?.error?.message?.includes('utilisée pour une machine')) {
          this.checkStrategyUsage(id);
        } else {
          showNotif(error?.error?.message || '❌ Erreur serveur lors de la suppression', 'error');
        }
      }
    });
  }

  getLabel(field: string): string {
    return field.replace(/([A-Z])/g, ' $1').trim();
  }

  onTypeStrategieChange(strategy: any): void {
    if (strategy.typeStrategie === 'court terme') {
      strategy.quotaJourSMS = 0;
      strategy.quotaJourMMS = 0;
      if ((strategy.typeEnvoi === 'SMS' || strategy.typeEnvoi === 'SMS & MMS') && strategy.quotaFixeSMS === 0) {
        strategy.quotaFixeSMS = 1;
      }
      if ((strategy.typeEnvoi === 'MMS' || strategy.typeEnvoi === 'SMS & MMS') && strategy.quotaFixeMMS === 0) {
        strategy.quotaFixeMMS = 1;
      }
    } else if (strategy.typeStrategie === 'long terme') {
      strategy.quotaFixeSMS = 0;
      strategy.quotaFixeMMS = 0;
      strategy.minuteDebutSMS = '';
      strategy.minuteFinSMS = '';
      strategy.minuteDebutMMS = '';
      strategy.minuteFinMMS = '';
      if ((strategy.typeEnvoi === 'SMS' || strategy.typeEnvoi === 'SMS & MMS') && strategy.quotaJourSMS === 0) {
        strategy.quotaJourSMS = 1;
      }
      if ((strategy.typeEnvoi === 'MMS' || strategy.typeEnvoi === 'SMS & MMS') && strategy.quotaJourMMS === 0) {
        strategy.quotaJourMMS = 1;
      }
    } else {
      strategy.quotaJourSMS = 0;
      strategy.quotaJourMMS = 0;
      strategy.quotaFixeSMS = 0;
      strategy.quotaFixeMMS = 0;
      strategy.minuteDebutSMS = '';
      strategy.minuteFinSMS = '';
      strategy.minuteDebutMMS = '';
      strategy.minuteFinMMS = '';
    }
  }

  onTypeEnvoiChange(strategy: any): void {
    if (strategy.typeEnvoi === 'SMS' || strategy.typeEnvoi === 'SMS & MMS') {
      if (strategy.quotaMinSMS === 0) strategy.quotaMinSMS = 1;
      if (strategy.quotaHSMS === 0) strategy.quotaHSMS = 1;
    }
    
    if (strategy.typeEnvoi === 'MMS' || strategy.typeEnvoi === 'SMS & MMS') {
      if (strategy.quotaMinMMS === 0) strategy.quotaMinMMS = 1;
      if (strategy.quotaHMMS === 0) strategy.quotaHMMS = 1;
    }

    this.onTypeStrategieChange(strategy);
  }

  validateStrategyData(data: any): { isValid: boolean; message?: string } {
    if (!data.operateur || !data.typeStrategie || !data.typeEnvoi) {
      return { isValid: false, message: 'Champs obligatoires manquants : opérateur, type de stratégie, type d\'envoi' };
    }

    if (!data.nbMaxPuceSimultanee || data.nbMaxPuceSimultanee < 1) {
      return { isValid: false, message: 'Le nombre de puces simultanées est obligatoire et doit être >= 1' };
    }

    if (data.typeEnvoi === 'SMS' || data.typeEnvoi === 'SMS & MMS') {
      if (!data.quotaMinSMS || data.quotaMinSMS < 1) {
        return { isValid: false, message: 'Le quota par minute SMS est obligatoire et doit être >= 1' };
      }
      if (!data.quotaHSMS || data.quotaHSMS < 1) {
        return { isValid: false, message: 'Le quota par heure SMS est obligatoire et doit être >= 1' };
      }
    }

    if (data.typeEnvoi === 'MMS' || data.typeEnvoi === 'SMS & MMS') {
      if (!data.quotaMinMMS || data.quotaMinMMS < 1) {
        return { isValid: false, message: 'Le quota par minute MMS est obligatoire et doit être >= 1' };
      }
      if (!data.quotaHMMS || data.quotaHMMS < 1) {
        return { isValid: false, message: 'Le quota par heure MMS est obligatoire et doit être >= 1' };
      }
    }

    if (data.typeStrategie === 'long terme') {
      if (data.typeEnvoi === 'SMS' || data.typeEnvoi === 'SMS & MMS') {
        if (!data.quotaJourSMS || data.quotaJourSMS < 1) {
          return { isValid: false, message: 'Le quota par jour SMS est obligatoire pour les stratégies long terme et doit être >= 1' };
        }
      }
      if (data.typeEnvoi === 'MMS' || data.typeEnvoi === 'SMS & MMS') {
        if (!data.quotaJourMMS || data.quotaJourMMS < 1) {
          return { isValid: false, message: 'Le quota par jour MMS est obligatoire pour les stratégies long terme et doit être >= 1' };
        }
      }
    } else if (data.typeStrategie === 'court terme') {
      if (data.typeEnvoi === 'SMS' || data.typeEnvoi === 'SMS & MMS') {
        if (!data.quotaFixeSMS || data.quotaFixeSMS < 1) {
          return { isValid: false, message: 'Le quota fixe SMS est obligatoire pour les stratégies court terme et doit être >= 1' };
        }
      }
      if (data.typeEnvoi === 'MMS' || data.typeEnvoi === 'SMS & MMS') {
        if (!data.quotaFixeMMS || data.quotaFixeMMS < 1) {
          return { isValid: false, message: 'Le quota fixe MMS est obligatoire pour les stratégies court terme et doit être >= 1' };
        }
      }
    }

    return { isValid: true };
  }

  shouldShowQuotas(strategy: any): boolean {
    if (strategy.typeEnvoi === 'SMS') {
      return (strategy.quotaMinSMS > 0 || strategy.quotaFixeSMS > 0);
    } else if (strategy.typeEnvoi === 'MMS') {
      return (strategy.quotaMinMMS > 0 || strategy.quotaFixeMMS > 0);
    } else if (strategy.typeEnvoi === 'SMS & MMS') {
      return (strategy.quotaMinSMS > 0 || strategy.quotaFixeSMS > 0 || 
              strategy.quotaMinMMS > 0 || strategy.quotaFixeMMS > 0);
    }
    return false;
  }
}
